import type { MatchScenario } from "../results/ResultScenarioRouter";

/**
 * Properties for the "Form Funnel - Results Shown" Mixpanel event.
 *
 * Field naming: we use `matchedProgramId` / `matchedProviderId` (not `programId` /
 * `providerId`) to avoid collision with the funnel-level `programId` that
 * `FormFunnelTrackingProvider.trackEvent` applies to existing funnel events.
 * Keeping separate names preserves both the configured and matched program
 * when vocation fallback surfaces a different program (see ENG-6212).
 */
export interface ResultsShownTrackingProperties extends Record<string, unknown> {
  scenarioType: MatchScenario["type"];
  matchedProgramId: string | null;
  matchedProgramName: string | null;
  matchedProviderId: string | null;
  matchedProviderName: string | null;
  shownProgramIds?: string[];
  shownProgramNames?: string[];
  shownProviderIds?: string[];
  shownProviderNames?: string[];
  shownResultRanks?: number[];
  /**
   * True when this NO_MATCH came from the resolver's error fallback (an
   * exception converted into a clean NO_MATCH) rather than a genuine no-match.
   * Lets dashboards split real "no programs near you" from infra/config
   * failures like the empty-DREAM_PORTAL_URL preview bug. ENG-7461.
   */
  isErrorFallback: boolean;
}

export const getResultsShownTrackingProperties = (
  scenario: MatchScenario,
  isErrorFallback = false
): ResultsShownTrackingProperties => {
  if (scenario.type === "CLICKOUT") {
    return {
      scenarioType: scenario.type,
      matchedProgramId: scenario.program.id,
      matchedProgramName: scenario.program.name,
      matchedProviderId: scenario.program.provider.id,
      matchedProviderName: scenario.program.provider.name,
      isErrorFallback,
    };
  }

  if (scenario.type === "HOSTED") {
    return {
      scenarioType: scenario.type,
      matchedProgramId: scenario.program.id,
      matchedProgramName: scenario.program.name,
      matchedProviderId: scenario.program.provider.id,
      matchedProviderName: scenario.program.provider.fullName,
      isErrorFallback,
    };
  }

  if (scenario.type === "MULTI_RESULT") {
    const firstResult = scenario.results[0];
    const getProviderName = (result: (typeof scenario.results)[number]) =>
      result.type === "CLICKOUT" ? result.program.provider.name : result.program.provider.fullName;
    return {
      scenarioType: scenario.type,
      matchedProgramId: firstResult?.program.id ?? null,
      matchedProgramName: firstResult?.program.name ?? null,
      matchedProviderId: firstResult?.program.provider.id ?? null,
      matchedProviderName: firstResult ? getProviderName(firstResult) : null,
      shownProgramIds: scenario.results.map((result) => result.program.id),
      shownProgramNames: scenario.results.map((result) => result.program.name),
      shownProviderIds: scenario.results.map((result) => result.program.provider.id),
      shownProviderNames: scenario.results.map(getProviderName),
      shownResultRanks: scenario.results.map((result) => result.rank),
      isErrorFallback,
    };
  }

  return {
    scenarioType: scenario.type,
    matchedProgramId: null,
    matchedProgramName: null,
    matchedProviderId: null,
    matchedProviderName: null,
    isErrorFallback,
  };
};
